import inventory from '../generated/componentInventory.json';

// Admin-only view of the shared component/hook library and where each is used.
// Data is produced by scripts/component-inventory.js (regenerated on each build
// via the `prebuild` npm script; run `npm run components:report` to refresh locally).

const card = { background: '#fff', border: '1px solid #e8ecef', borderRadius: '8px', padding: '14px 16px', marginBottom: '12px' };
const badge = (bg, color) => ({ background: bg, color, borderRadius: '10px', padding: '2px 8px', fontSize: '11px', whiteSpace: 'nowrap' });

function ModuleCard({ mod }) {
  const exports = [mod.exports.default ? 'default' : null, ...mod.exports.named].filter(Boolean);
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>{mod.file}</span>
        <span style={badge(mod.importers.length ? '#eafaf1' : '#fdedec', mod.importers.length ? '#1e8449' : '#c0392b')}>
          {mod.importers.length} importer{mod.importers.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px' }}>
        {exports.map(e => (
          <span key={e} style={badge('#eaf2fb', '#2471a3')}>{e}</span>
        ))}
      </div>
      {mod.importers.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#c0392b' }}>⚠ Not imported anywhere — possible dead code.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            {mod.importers.map(imp => (
              <tr key={imp.file}>
                <td style={{ padding: '3px 8px 3px 0', color: '#34495e', fontFamily: 'monospace', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{imp.file}</td>
                <td style={{ padding: '3px 0', color: '#7f8c8d' }}>{imp.names.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function ComponentLibrary() {
  const { modules, issues, generatedAt } = inventory;
  const byDir = (dir) => modules.filter(m => m.dir === dir);
  const issueCount = issues.staleImports.length + issues.possibleDuplicates.length + issues.unused.length;

  return (
    <div style={{ maxWidth: '900px' }}>
      <h2 style={{ margin: '0 0 4px' }}>Component Library</h2>
      <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#7f8c8d' }}>
        Inventory of shared UI in <code>src/components/</code> and <code>src/hooks/</code>, and every file that imports each one.
        Regenerated on each build; run <code>npm run components:report</code> to refresh locally.
      </p>
      <p style={{ margin: '0 0 24px', fontSize: '12px', color: '#aaa' }}>
        Generated {generatedAt ? new Date(generatedAt).toLocaleString() : '—'} · {modules.length} modules
      </p>

      {/* ── Issues ── */}
      <div style={{ marginBottom: '28px' }}>
        <h3 style={{ margin: '0 0 10px' }}>
          Issues {issueCount === 0 ? <span style={{ color: '#27ae60', fontSize: '14px' }}>✓ none</span> : <span style={{ color: '#e67e22', fontSize: '14px' }}>{issueCount}</span>}
        </h3>
        {issues.staleImports.length > 0 && (
          <div style={{ ...card, borderLeft: '4px solid #c0392b' }}>
            <div style={{ fontWeight: '600', color: '#c0392b', fontSize: '13px', marginBottom: '6px' }}>✗ Stale imports (path no longer resolves)</div>
            {issues.staleImports.map((s, i) => (
              <div key={i} style={{ fontSize: '12px', fontFamily: 'monospace', color: '#555' }}>{s.file} → {s.spec}</div>
            ))}
          </div>
        )}
        {issues.possibleDuplicates.length > 0 && (
          <div style={{ ...card, borderLeft: '4px solid #e67e22' }}>
            <div style={{ fontWeight: '600', color: '#b9770e', fontSize: '13px', marginBottom: '6px' }}>⚠ Possible local re-definitions of a shared export</div>
            {issues.possibleDuplicates.map((d, i) => (
              <div key={i} style={{ fontSize: '12px', color: '#555' }}>
                <code>{d.file}</code> defines <strong>{d.name}</strong> — also shared in <code>{d.shared}</code>
              </div>
            ))}
          </div>
        )}
        {issues.unused.length > 0 && (
          <div style={{ ...card, borderLeft: '4px solid #e67e22' }}>
            <div style={{ fontWeight: '600', color: '#b9770e', fontSize: '13px', marginBottom: '6px' }}>⚠ Unused shared modules (no importers)</div>
            {issues.unused.map((f, i) => <div key={i} style={{ fontSize: '12px', fontFamily: 'monospace', color: '#555' }}>{f}</div>)}
          </div>
        )}
        {issueCount === 0 && (
          <div style={{ ...card, color: '#27ae60', fontSize: '13px' }}>Everything is wired up cleanly — no stale imports, duplicate definitions, or unused modules.</div>
        )}
      </div>

      <h3 style={{ margin: '0 0 10px' }}>Components <span style={{ color: '#aaa', fontSize: '14px' }}>({byDir('components').length})</span></h3>
      {byDir('components').map(m => <ModuleCard key={m.file} mod={m} />)}

      <h3 style={{ margin: '24px 0 10px' }}>Hooks <span style={{ color: '#aaa', fontSize: '14px' }}>({byDir('hooks').length})</span></h3>
      {byDir('hooks').map(m => <ModuleCard key={m.file} mod={m} />)}
    </div>
  );
}
